import { useApiUrl, useGo } from "@refinedev/core";
import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Compass,
  Inbox,
  RefreshCw,
  RotateCcw,
  Skull,
  Zap,
} from "@/components/ui/icon";

import { PageHeader } from "@/components/layout/PageHeader";

import {
  EVIDENCE_POLARITY,
  PACKAGE_MATURITY_STAGES,
  SIGNAL_STATUS,
  type EvidencePolarity,
  type OperationsDashboard,
  type PackageMaturityStage,
  type SignalStatus,
} from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/app";
import { ErrorState } from "@/components/ui/states";

/**
 * AOS v0.2 advisory metrics (D3 — context coverage is a *metric*, never a gate).
 * `ContextCoverage` mirrors `GET /api/dashboard/context-coverage` (contract §3.4,
 * owned by B5). The package/claim/evidence breakdowns arrive as additive optional
 * fields on `OperationsDashboard`. Both are read defensively so the operations
 * page never errors when the backend halves are still in flight.
 */
interface ContextCoverage {
  artifacts_total: number;
  artifacts_with_context: number;
  coverage_ratio: number; // 0..1
  contexts_by_maturity: Record<string, number>;
  signals_by_status: Partial<Record<SignalStatus, number>>;
}

/** Additive v0.2 fields B5 layers onto the v0.1 `OperationsDashboard` payload. */
interface OperationsDashboardV2 {
  packages_by_maturity?: Partial<Record<PackageMaturityStage, number>>;
  open_claims?: number;
  evidence_by_polarity?: Partial<Record<EvidencePolarity, number>>;
}

/** Fetch JSON from the worker API, sending the session cookie. */
async function fetchJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const res = await fetch(url, { credentials: "include", signal });
  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

/** Like `fetchJson` but resolves `null` on any failure (advisory metrics). */
async function fetchJsonOptional<T>(url: string, signal: AbortSignal): Promise<T | null> {
  try {
    return await fetchJson<T>(url, signal);
  } catch {
    return null;
  }
}

/** A labelled count breakdown rendered as a wrapped row of badges. */
function Breakdown({
  counts,
  order,
}: {
  counts: Partial<Record<string, number>>;
  order: readonly string[];
}) {
  const entries = order
    .map((key) => [key, counts[key] ?? 0] as const)
    .filter(([, n]) => n > 0);
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No data yet.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([key, n]) => (
        <Badge key={key} variant="secondary" className="font-normal">
          <span className="font-mono text-[10px] uppercase tracking-wide">{key}</span>
          <span className="ml-1.5 font-semibold">{n}</span>
        </Badge>
      ))}
    </div>
  );
}

function StatTile({
  label,
  value,
  icon,
  tone = "default",
  onClick,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone?: "default" | "danger";
  onClick?: () => void;
}) {
  const danger = tone === "danger" && value > 0;
  return (
    <Card
      className={onClick ? "cursor-pointer transition-colors hover:bg-muted/40" : undefined}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <span className={danger ? "text-destructive" : "text-muted-foreground"}>{icon}</span>
      </CardHeader>
      <CardContent>
        <div className={danger ? "text-2xl font-bold text-destructive" : "text-2xl font-bold"}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Operations (D6) — spec §15 Observability / Operations Dashboard.
 * Reads `GET /api/dashboard/operations` (DTO `OperationsDashboard`) and exposes
 * the required signals: events today, failed events, rule runs today/failed,
 * agent runs today/failed, queue backlog, dead-letter, and last connector sync.
 *
 * Failure tiles deep-link to the filtered failed-run lists (events / rule-runs /
 * agent-runs resources, owned by D5) where per-row replay & retry actions live.
 */
export function Operations() {
  const apiUrl = useApiUrl();
  const go = useGo();
  const [data, setData] = useState<OperationsDashboard | null>(null);
  const [coverage, setCoverage] = useState<ContextCoverage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    // Context coverage is advisory (D3) — fetched beside the ops payload but
    // never allowed to fail the page; resolves null when B5's endpoint is absent.
    Promise.all([
      fetchJson<OperationsDashboard>(`${apiUrl}/dashboard/operations`, controller.signal),
      fetchJsonOptional<ContextCoverage>(
        `${apiUrl}/dashboard/context-coverage`,
        controller.signal,
      ),
    ])
      .then(([ops, cov]) => {
        setData(ops);
        setCoverage(cov);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to load operations dashboard");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [apiUrl, refreshKey]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Operations"
          subtitle="System health, dead-letter queues, and effect retries at a glance."
          icon="operations"
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Operations"
          subtitle="System health, dead-letter queues, and effect retries at a glance."
          icon="operations"
        />
        <ErrorState message={error ?? "No data returned."} onRetry={refresh} />
      </div>
    );
  }

  const v2 = data as OperationsDashboard & OperationsDashboardV2;
  const coveragePct =
    coverage != null ? Math.round(coverage.coverage_ratio * 100) : null;
  // The advisory v0.2 block renders only when at least one backend half is live.
  const hasV2Metrics =
    coverage != null ||
    v2.packages_by_maturity != null ||
    v2.open_claims != null ||
    v2.evidence_by_polarity != null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operations"
        subtitle="System health, dead-letter queues, and effect retries at a glance."
        icon="operations"
        actions={
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="size-4" /> Refresh
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Events today" value={data.events_today} icon={<Zap className="size-4" />} />
        <StatTile
          label="Failed events"
          value={data.failed_events}
          icon={<AlertTriangle className="size-4" />}
          tone="danger"
          onClick={() =>
            go({ to: { resource: "events", action: "list" }, query: { status: "failed" } })
          }
        />
        <StatTile label="Rule runs today" value={data.rule_runs_today} icon={<RotateCcw className="size-4" />} />
        <StatTile
          label="Failed rule runs"
          value={data.failed_rule_runs}
          icon={<AlertTriangle className="size-4" />}
          tone="danger"
          onClick={() =>
            go({ to: { resource: "rule-runs", action: "list" }, query: { status: "failed" } })
          }
        />
        <StatTile label="Agent runs today" value={data.agent_runs_today} icon={<Zap className="size-4" />} />
        <StatTile
          label="Failed agent runs"
          value={data.failed_agent_runs}
          icon={<AlertTriangle className="size-4" />}
          tone="danger"
          onClick={() =>
            go({ to: { resource: "agent-runs", action: "list" }, query: { status: "failed" } })
          }
        />
        <StatTile label="Queue backlog" value={data.queue_backlog} icon={<Inbox className="size-4" />} />
        <StatTile
          label="Dead-letter events"
          value={data.dead_letter_count}
          icon={<Skull className="size-4" />}
          tone="danger"
        />
      </div>

      {hasV2Metrics && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Compass className="size-4 text-muted-foreground" /> Context coverage
              <Badge variant="outline" className="ml-1 font-normal">
                advisory
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-md border border-border p-4">
                <div className="text-sm text-muted-foreground">Coverage</div>
                <div className="text-2xl font-bold">
                  {coveragePct != null ? `${coveragePct}%` : "—"}
                </div>
                {coverage != null && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {coverage.artifacts_with_context} / {coverage.artifacts_total} artifacts
                  </div>
                )}
              </div>
              <div className="rounded-md border border-border p-4">
                <div className="text-sm text-muted-foreground">Open claims</div>
                <div className="text-2xl font-bold">{v2.open_claims ?? "—"}</div>
              </div>
              <div className="rounded-md border border-border p-4 sm:col-span-2">
                <div className="mb-2 text-sm text-muted-foreground">Signals by status</div>
                {coverage != null ? (
                  <Breakdown counts={coverage.signals_by_status} order={SIGNAL_STATUS} />
                ) : (
                  <p className="text-sm text-muted-foreground">—</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-md border border-border p-4">
                <div className="mb-2 text-sm text-muted-foreground">Packages by maturity</div>
                {v2.packages_by_maturity != null ? (
                  <Breakdown counts={v2.packages_by_maturity} order={PACKAGE_MATURITY_STAGES} />
                ) : (
                  <p className="text-sm text-muted-foreground">—</p>
                )}
              </div>
              <div className="rounded-md border border-border p-4">
                <div className="mb-2 text-sm text-muted-foreground">Evidence by polarity</div>
                {v2.evidence_by_polarity != null ? (
                  <Breakdown counts={v2.evidence_by_polarity} order={EVIDENCE_POLARITY} />
                ) : (
                  <p className="text-sm text-muted-foreground">—</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Connector sync</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Last sync</span>
            <span>
              {data.last_connector_sync_at
                ? new Date(data.last_connector_sync_at).toLocaleString()
                : "Never"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Last status</span>
            {data.last_connector_sync_status ? (
              <StatusBadge status={data.last_connector_sync_status} />
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Replay & retry</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => go({ to: { resource: "events", action: "list" }, query: { status: "failed" } })}
          >
            <RotateCcw className="size-4" /> Replay failed events
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => go({ to: { resource: "rule-runs", action: "list" }, query: { status: "failed" } })}
          >
            <RotateCcw className="size-4" /> Inspect failed rule runs
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => go({ to: { resource: "agent-runs", action: "list" }, query: { status: "failed" } })}
          >
            <RotateCcw className="size-4" /> Retry failed agent runs
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default Operations;
